import { GameType } from '@daymade/common-lib/dbEnums';
import { TLotteryDrawEntry } from '@daymade/common-lib/dbJsonModels';
import { DaymadeDrawTypePlanCode } from 'constants/Draw';
import { useUserStore } from 'hooks/stores/useUserStore';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { IRaffleTicketEntry, ITicketEntry } from 'types/Ticket';
import { orderApiClient } from 'utils/apiClient';
import { convertMicroCostToLegacy } from 'utils/cost/convertMicroCost';
import HttpStatusCode from 'utils/HttpStatusCode';

interface DataProps {
  loading: boolean;
  orderId?: string;
  liteConfirmationPage: boolean;
  freeConfirmationPage: boolean;
  premiumConfirmationPage: boolean;
  tuesdayDraw: boolean;
  fridayDraw: boolean;
  drawDates: string[];
  usedFreeSubscriptionDeal: boolean;
  entries: TLotteryDrawEntry[];
  promoCodeEntries: ITicketEntry[] | IRaffleTicketEntry[];
  subscriptionEnabled: boolean;
  orderCostMicro: number;
  subscriptionCosts: {
    total: number;
    estimatedRenewalDate: string;
    totalWithoutDiscount: number;
    boostCost: number;
  };
  monthlyPayments: boolean;
  drawTypePlanCode?: DaymadeDrawTypePlanCode;
}

export const useConfirmationData = (): DataProps => {
  const router = useRouter();

  const { currentUser, activeSubscriptions } = useUserStore();

  const [data, setData] = useState<DataProps>({
    loading: true,
    premiumConfirmationPage: false,
    liteConfirmationPage: false,
    freeConfirmationPage: false,
    tuesdayDraw: false,
    fridayDraw: false,
    drawDates: [],
    usedFreeSubscriptionDeal: false,
    entries: [],
    promoCodeEntries: [],
    subscriptionEnabled: false,
    orderCostMicro: 0,
    subscriptionCosts: {
      total: 0,
      estimatedRenewalDate: '',
      totalWithoutDiscount: 0,
      boostCost: 0,
    },
    monthlyPayments: false,
    drawTypePlanCode: undefined,
  });

  useEffect(() => {
    (async () => {
      const orderId =
        (router.query.orderId as string) ||
        (router.query.freeSubscriptionId as string) ||
        undefined;

      if (!router.isReady || !orderId || !currentUser) {
        return;
      }

      let entries: TLotteryDrawEntry[] = [],
        tuesdayDraw = false,
        fridayDraw = false,
        promoCodeEntries: any[] = [],
        subscriptionEnabled = false,
        orderCostMicro = 0,
        liteConfirmationPage = false,
        freeConfirmationPage = false,
        premiumConfirmationPage = false,
        usedFreeSubscriptionDeal = false,
        monthlyPayments,
        subscriptionCosts: any = {},
        drawDates: string[] = [];

      const orderDetails = await orderApiClient.auth.getOrderDetails({
        params: { order_id: orderId },
      });

      if (orderDetails.status !== HttpStatusCode.OK) {
        console.error('Failed to fetch order details:', orderDetails);
        return;
      }

      const order = orderDetails.body.order;
      const drawOrder = orderDetails.body.drawOrder;
      const firstDraw = orderDetails.body.firstDraw;
      const drawSchedulePlans = orderDetails.body.drawSchedulePlans;

      if (
        !drawOrder ||
        !firstDraw ||
        !drawSchedulePlans ||
        drawSchedulePlans.length === 0
      ) {
        console.error('Incomplete order details:', orderDetails);
        return;
      }

      const drawDays = drawSchedulePlans.map(
        ({ drawSchedule }) => drawSchedule.code,
      );

      tuesdayDraw = drawDays.includes('TUESDAY');
      fridayDraw = drawDays.includes('FRIDAY');
      promoCodeEntries = [];
      subscriptionEnabled = order.isSubscription;
      orderCostMicro = convertMicroCostToLegacy(order.totalFiatAmount);
      liteConfirmationPage =
        drawSchedulePlans[0].drawTypePlan.code === DaymadeDrawTypePlanCode.LITE;
      freeConfirmationPage =
        drawSchedulePlans[0].drawTypePlan.code === DaymadeDrawTypePlanCode.FREE;
      premiumConfirmationPage =
        drawSchedulePlans[0].drawTypePlan.code ===
        DaymadeDrawTypePlanCode.PREMIUM;
      usedFreeSubscriptionDeal = false;
      drawDates = firstDraw.drawDate ? [firstDraw.drawDate.toISOString()] : [];
      subscriptionCosts = {
        total: convertMicroCostToLegacy(order.totalFiatAmount),
        estimatedRenewalDate: orderDetails.body.subscription?.nextPaymentDate
          ? new Date(
              orderDetails.body.subscription?.nextPaymentDate,
            ).toISOString()
          : '',
        totalWithoutDiscount: convertMicroCostToLegacy(drawOrder.price),
        boostCost: 0,
      };

      if (drawOrder.entries.every((e) => e.type === GameType.LOTTERY)) {
        entries = drawOrder.entries;
      }

      setData({
        loading: false,
        orderId: orderId,
        liteConfirmationPage,
        freeConfirmationPage,
        premiumConfirmationPage,
        tuesdayDraw,
        fridayDraw,
        drawDates,
        entries,
        usedFreeSubscriptionDeal,
        promoCodeEntries,
        subscriptionEnabled,
        orderCostMicro,
        subscriptionCosts,
        monthlyPayments: monthlyPayments || false,
        drawTypePlanCode: drawSchedulePlans[0].drawTypePlan
          .code as DaymadeDrawTypePlanCode,
      });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, router.query, currentUser]);

  return data;
};
